import { useMemo } from "react";
import {
  FormDataConsumer,
  NumberInput,
  required,
  SimpleForm,
  TextInput,
  useTranslate,
} from "react-admin";
import resourceNames from "../../../common/data/resourceNames";
import { useAppData } from "../../../common/hooks/useAppData";
import { useQueryParams } from "../../../common/hooks/useQueryParams";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { FormToolbar } from "../../common/form/FormToolbar";
import "../../common/styles/form-style.css";
import { DriverAndCoDriverInput } from "./DriverAndCoDriverInput";
import { EventLocationInput } from "./EventLocationInput";
import { EventStatusAndTimePicker } from "./EventStatusAndTimePicker";
import { FormInformation } from "./FormInformation";
import { VehicleDataInput } from "./VehicleDataInput";

interface Props {
  record?: any;
  isEdit?: boolean;
}

export const EventMasterForm = (props: Props) => {
  const { isEdit, ...rest } = props;
  const { record } = rest;
  const params = useQueryParams();
  const source = JSON.parse(params.get("source") ?? "{}");

  useSearchFields(undefined, ["name"], resourceNames.ADMIN_VEHICLES_VIEW);
  useSearchFields(
    undefined,
    ["driverName", "driverId"],
    resourceNames.ADMIN_DRIVERS_VIEW
  );

  const translate = useTranslate();
  const eventTypeCodesAppData = useAppData((x) => x.eventTypeCodes);
  const eventTypeCodes = useMemo(
    () =>
      Object.getOwnPropertyNames(eventTypeCodesAppData).map((x) => ({
        id: x,
        name: translate(`appData.eventTypeCodes.${x}`, { _: x }),
      })),
    [translate, eventTypeCodesAppData]
  );

  const eventTypeCodesWithNote = [
    "ChangeToOffDutyStatus",
    "ChangeToSleeperBerthStatus",
    "ChangeToOnDutyNotDrivingStatus",
  ];

  const validateSequenceNumber = (value: any, formData: any) => {
    if (!value) {
      return undefined;
    }

    if (typeof value !== "string") {
      return undefined;
    }

    const number = parseInt(value, 16);
    if (number < 1 || number > 65535) {
      return "Must be between 1 and 65535";
    }

    return undefined;
  };

  return (
    <SimpleForm
      className="form-create-edit"
      variant="outlined"
      margin="dense"
      toolbar={<FormToolbar showDeleteButton={false} />}
      {...rest}
    >
      <DriverAndCoDriverInput isEdit={isEdit} />
      <EventStatusAndTimePicker
        isEdit={isEdit}
        defaultTime={record?.date}
        timeZone={record?.homeTerminalTimeZone ?? source.homeTerminalTimeZone}
        eventTypeCodes={eventTypeCodes}
      />
      <EventLocationInput />
      <VehicleDataInput />
      <NumberInput
        validate={[required(), validateSequenceNumber]}
        source="eventSequenceIdNumber"
        label="Event sequence number"
        format={(x: string) => (x ? parseInt(x, 16) % 65536 : null)}
        parse={(x: any) => Number(x).toString(16).toUpperCase()}
        min="0"
      />
      <FormDataConsumer>
        {({ formData, ...formDataRest }) => (
          <>
            {eventTypeCodesWithNote.includes(formData.eventTypeCode) ? (
              <TextInput
                {...formDataRest}
                validate={required()}
                source="note"
              />
            ) : (
              <></>
            )}
            <FormInformation
              homeTerminalTimeZone={
                record?.homeTerminalTimeZone ?? source.homeTerminalTimeZone
              }
              startTime={formData.startTime}
              driverId={formData.driverId}
              eventTypeCodes={eventTypeCodes}
              {...formDataRest}
            />
          </>
        )}
      </FormDataConsumer>
    </SimpleForm>
  );
};
